/**
 * Release notes contracts (SCH-2340).
 *
 *   GET /v1/system/release-notes?pipeline=backend|web|mobile|platform&after=<cursor>   super-admin
 *
 * A read-only view of Linear releases from the release pipelines: "Backend" and
 * "Web" (continuous, one release per production push), "Mobile" (scheduled, one
 * release per app version), and "Platform" (backend + web before SCH-2445 split it;
 * history only). Linear is the
 * only source — nothing is stored on our side. The backend maps Linear's shape
 * to this one, so readers never see a raw Linear node.
 *
 * Newest first, 25 per page. `pageInfo.endCursor` is passed back as `after` to
 * read the next page.
 */

import { z } from 'zod'

/** Page size of the release list. */
export const RELEASE_NOTES_PAGE_SIZE = 25

/** The pipelines a caller can filter by. Omitting the filter returns all of them. */
export const ReleaseNotesPipelineFilterSchema = z.enum(['backend', 'web', 'mobile', 'platform'])
export type ReleaseNotesPipelineFilter = z.infer<typeof ReleaseNotesPipelineFilterSchema>

/** A release's pipeline; `other` is any pipeline added in Linear that this page does not know yet. */
export const ReleaseNotesPipelineSchema = z.enum(['backend', 'web', 'mobile', 'platform', 'other'])
export type ReleaseNotesPipeline = z.infer<typeof ReleaseNotesPipelineSchema>

/** The issue type, read from its `feature` / `improvement` / `defect` label. */
export const ReleaseNotesIssueTypeSchema = z.enum(['feature', 'improvement', 'defect'])
export type ReleaseNotesIssueType = z.infer<typeof ReleaseNotesIssueTypeSchema>

export const ListReleaseNotesRequestSchema = z.object({
  pipeline: ReleaseNotesPipelineFilterSchema.optional(),
  /** `pageInfo.endCursor` of the previous page. */
  after: z.string().min(1).optional()
})
export type ListReleaseNotesRequest = z.infer<typeof ListReleaseNotesRequestSchema>

export const ReleaseNotesIssueSchema = z.object({
  /** e.g. `SCH-2340`. */
  identifier: z.string(),
  title: z.string(),
  url: z.string(),
  stateName: z.string().nullable(),
  /** Linear workflow state type: triage | backlog | unstarted | started | completed | canceled. */
  stateType: z.string().nullable(),
  labels: z.array(z.string()),
  type: ReleaseNotesIssueTypeSchema.nullable(),
  /** True when the issue carries the `internal` label (not customer-facing). */
  internal: z.boolean()
})
export type ReleaseNotesIssue = z.infer<typeof ReleaseNotesIssueSchema>

export const ReleaseNotesReleaseSchema = z.object({
  /** Linear release id. */
  id: z.string(),
  /** e.g. `Platform 2026.09.26.1` or `Mobile 1.8.25`. */
  name: z.string(),
  version: z.string().nullable(),
  /** The release in Linear. */
  url: z.string(),
  pipeline: ReleaseNotesPipelineSchema,
  /** Pipeline name as Linear has it. */
  pipelineName: z.string(),
  /** `type` is planned | started | completed | canceled; `name` is the workspace's own label for the stage. */
  stage: z.object({ name: z.string(), type: z.string() }),
  createdAt: z.string(),
  completedAt: z.string().nullable(),
  description: z.string().nullable(),
  /** Generated release notes, markdown. Null until Linear has generated them. */
  notesMarkdown: z.string().nullable(),
  /** Linear's generation status for the notes, when a note exists. */
  notesStatus: z.string().nullable(),
  /** Total issues in the release; `issues` holds at most the first 100. */
  issueCount: z.number().int().nonnegative(),
  issues: z.array(ReleaseNotesIssueSchema)
})
export type ReleaseNotesRelease = z.infer<typeof ReleaseNotesReleaseSchema>

export const ListReleaseNotesResponseSchema = z.object({
  releases: z.array(ReleaseNotesReleaseSchema),
  pageInfo: z.object({
    hasNextPage: z.boolean(),
    endCursor: z.string().nullable()
  })
})
export type ListReleaseNotesResponse = z.infer<typeof ListReleaseNotesResponseSchema>
